/*
 *--------------------------------------------------------------------------
 * reCAPTCHA v3
 *--------------------------------------------------------------------------
 *
 * Manual reCAPTCHA v3 integration for custom (Genesis) forms.
 *
 * The native "reCAPTCHA v3 by Miva" module validates covered actions
 * (e.g. ICST / Create Account) server-side, but its automatic client-side
 * token injection does not reliably attach to forms that are manipulated by
 * JavaScript before submission, and the API is not always loaded on the
 * page. When no token is attached the module rejects the submission with
 * "Empty reCAPTCHA response".
 *
 * This helper loads the reCAPTCHA v3 API itself and attaches a `submit`
 * handler to each opted-in form. On submit it generates a fresh token,
 * writes it to the form's `g-recaptcha-response` field, then re-submits so
 * the token is present in the POST the Miva module validates. Hooking the
 * form `submit` event (rather than a button click) covers button clicks,
 * Enter-key submits, and programmatic submits alike.
 *
 * A form opts in by adding to its markup:
 *   - data-recaptcha-action="create_account"   (any v3 action label)
 *   - <input type="hidden" name="g-recaptcha-response" value="">
 *
 */

/**
 * Public reCAPTCHA v3 site key. Site keys are not secret and are intended to
 * be exposed in client-side code. The matching secret key lives in the Miva
 * reCAPTCHA module settings and is never shipped to the browser.
 *
 * @var String
 */
const SITE_KEY = '6LfV76ssAAAAAJImUOenllf8qmSMmyKb-41oM-B5';

/**
 * Source for the reCAPTCHA v3 API, pre-bound to the site key.
 *
 * @var String
 */
const API_SRC = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;

/**
 * Name of the hidden field the token is written to and the Miva module reads.
 *
 * @var String
 */
const TOKEN_FIELD_NAME = 'g-recaptcha-response';

/**
 * Dataset flag used to allow the re-submitted (tokenized) form through.
 *
 * @var String
 */
const PASSED_FLAG = 'recaptchaPassed';

/**
 * Cached promise so the API is only requested once per page.
 *
 * @var Promise|null
 */
let apiPromise = null;

/**
 * Load the reCAPTCHA v3 API once and resolve when `grecaptcha` is ready.
 *
 * Reuses an existing instance/script if the Miva module has already injected
 * one, otherwise injects its own. Polls for readiness to avoid races with a
 * script that may already be in flight.
 *
 * @returns {Promise<Object>} resolves with window.grecaptcha
 */
function loadApi () {
	if (apiPromise) {
		return apiPromise;
	}

	apiPromise = new Promise((resolve, reject) => {
		const alreadyLoading = Boolean(document.querySelector('script[src*="recaptcha/api.js"]'));

		if (!window.grecaptcha && !alreadyLoading) {
			const script = document.createElement('script');

			script.src = API_SRC;
			script.async = true;
			script.defer = true;
			script.onerror = () => reject(new Error('reCAPTCHA failed to load'));

			document.head.appendChild(script);
		}

		// Poll until the API reports ready. Covers the case where the script is
		// already present (Miva module) and may have loaded before this runs.
		const startedAt = Date.now();

		const waitForReady = () => {
			if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
				window.grecaptcha.ready(() => resolve(window.grecaptcha));
			} else if (Date.now() - startedAt > 10000) {
				reject(new Error('reCAPTCHA timed out while loading'));
			} else {
				setTimeout(waitForReady, 100);
			}
		};

		waitForReady();
	});

	return apiPromise;
}

/**
 * Re-submit a form that now carries its reCAPTCHA token. The flag prevents
 * the submit handler from intercepting this second submission.
 *
 * @param {HTMLFormElement} form
 */
function submitTokenizedForm (form) {
	form.dataset[PASSED_FLAG] = 'true';

	if (typeof form.requestSubmit === 'function') {
		form.requestSubmit();
	} else {
		form.submit();
	}
}

/**
 * `submit` handler for an opted-in form. Generates a v3 token, writes it to
 * the `g-recaptcha-response` field, then re-submits.
 *
 * @param {Event} event
 */
function onFormSubmit (event) {
	const form = event.currentTarget;

	// Second pass: the token has already been injected, allow the submission.
	if (form.dataset[PASSED_FLAG] === 'true') {
		return;
	}

	const action = form.dataset.recaptchaAction;
	const tokenField = form.querySelector(`input[name="${TOKEN_FIELD_NAME}"]`);

	// Misconfigured form — leave the submission untouched.
	if (!action || !tokenField) {
		return;
	}

	event.preventDefault();

	loadApi()
		.then((grecaptcha) => grecaptcha.execute(SITE_KEY, {action}))
		.then((token) => {
			tokenField.value = token;
			submitTokenizedForm(form);
		})
		.catch((error) => {
			// Surface the failure loudly rather than silently posting an empty
			// token. Still re-submit so the shopper isn't trapped; the Miva
			// module will reject if the response is genuinely missing.
			// eslint-disable-next-line no-console
			console.error('[recaptcha-v3] token generation failed:', error);
			submitTokenizedForm(form);
		});
}

/**
 * Attach reCAPTCHA v3 token generation to every form opted in via the
 * `data-recaptcha-action` attribute. Safe to call once on every page.
 */
export function initRecaptchaForms () {
	const forms = document.querySelectorAll('form[data-recaptcha-action]');

	if (!forms.length) {
		return;
	}

	for (const form of forms) {
		form.addEventListener('submit', onFormSubmit);
	}

	// Warm up the API so the badge renders and the first submit is instant.
	loadApi().catch((error) => {
		// eslint-disable-next-line no-console
		console.error('[recaptcha-v3] failed to preload:', error);
	});
}

export default initRecaptchaForms;
